import { FormLabel, forwardRef, Input, InputGroup, InputLeftElement, InputProps, Text } from '@chakra-ui/react';
import { ChangeEventHandler, ReactNode, useEffect, useState } from 'react';
import { WithFormControl, WithFormControlProps } from '../WithFormControl';
import { formatFloatValueToCurrencyInputValue } from './utils/formatNumberToCurrencyInputValue';
import { parseInputValueToFloatString } from './utils/parseInputValueToFloatString';

export interface CurrencyInputFieldProps extends Omit<InputProps, 'type' | 'value'> {
  label?: ReactNode;
  value?: string | number | null;
  currencySymbol?: ReactNode;
  rootProps?: WithFormControlProps;
  withoutFloating?: boolean;
  helperText?: string;
}

const MAX_SUPPORTED_SIZE = 24;

export const CurrencyInputField = forwardRef<CurrencyInputFieldProps, 'input'>(
  ({ label, value, onChange, currencySymbol = 'R$', rootProps, defaultValue, withoutFloating, helperText, ...props }, ref) => {
    const [localValue, setLocalValue] = useState<string>(defaultValue?.toString() ?? '');

    useEffect(() => {
      if (typeof value === 'undefined') {
        return;
      }

      setLocalValue(parseInputValueToFloatString(value?.toString() || ''));
    }, [value]);

    const handleChange: ChangeEventHandler<HTMLInputElement> = event => {
      const { value } = event.target;

      const parsedValue = parseInputValueToFloatString(value);

      Object.assign(event.target, { value: parsedValue });

      setLocalValue(parsedValue);
      onChange?.(event);
    };

    const shouldApplyDisabledOpacityOnSymbol = Boolean(props.isDisabled || rootProps?.isDisabled);

    return (
      <WithFormControl variant={withoutFloating ? undefined : 'floating'} {...rootProps}>
        <InputGroup display='flex' flexDirection='column'>
          {label && (
            <FormLabel color='text.main' fontSize={withoutFloating ? '14px' : undefined} m={withoutFloating ? 0 : undefined}>
              {label}
            </FormLabel>
          )}
          <InputLeftElement
            borderBottom='1px solid transparent'
            pointerEvents='none'
            opacity={shouldApplyDisabledOpacityOnSymbol ? 0.4 : 1}
            color='text.main'
            marginTop={5}
          >
            {currencySymbol}
          </InputLeftElement>

          <Input
            type='text'
            value={formatFloatValueToCurrencyInputValue(localValue)}
            maxLength={MAX_SUPPORTED_SIZE}
            onChange={handleChange}
            {...props}
            placeholder=' '
            ref={ref}
            variant='flushed'
          />
          {helperText ? (
            <Text fontSize='12px' color='gray.500' mt={1}>
              {helperText}
            </Text>
          ) : null}
        </InputGroup>
      </WithFormControl>
    );
  },
);
